///
/// EarthBrowser
///
/// Created by Matt Giger on 4/2/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type frustum } from "@stovokor/math/frustum"
import { matrix } from "@stovokor/math/matrix"
import { ray } from "@stovokor/math/ray"
import { normal, vec2, vec3 } from "@stovokor/math/vector"
import { CameraState } from "@stovokor/camera/CameraState"
import { type NodeBase } from "@stovokor/engine/NodeBase"

export interface CameraProjection {
	readonly width: number
	readonly height: number
	readonly projection: matrix
	setSize(width: number, height: number): void
}

export interface CameraControllerProtocol {
	target: NodeBase
	controlGranted(camera: Camera): void
	releaseControl(): void
	renderUpdate?(): void
}

export class Camera {
	public state = new CameraState()
	public projection: CameraProjection
	public viewMatrix = matrix.identity
	public projectionMatrix = matrix.identity
	public modelViewProjection = matrix.identity
	private modelViewProjectionInverse?: matrix
	private initialized: boolean = false
	private _frustum?: frustum
	private frustumSource?: matrix

	/**
	 * View-space culling frustum (extracted from the projection alone, so
	 * it is tested against view-space bounds). Rebuilt only when the
	 * projection matrix object changes; six normalized planes otherwise
	 * cost an allocation per render state per frame
	 */
	public get frustum(): frustum {
		if(this._frustum === undefined || this.frustumSource !== this.projectionMatrix) {
			this._frustum = this.projectionMatrix.frustum()
			this.frustumSource = this.projectionMatrix
		}
		return this._frustum
	}

	constructor(projection: CameraProjection) {
		this.projection = projection
	}

	public get position(): vec3 {
		return this.state.position
	}

	/**
	 * Rebuild the view and projection matrices from the current state.
	 * Returns true when the combined matrix changed, i.e. the camera moved
	 * or the projection (size, fov, lens shift) changed since the last call
	 */
	public updateProjection(): boolean {
		// Keep the previous matrix objects when the values are unchanged:
		// per-node matrix caches compare these by identity, so a static
		// camera lets every static node skip its multiplies
		const view = this.state.lookToward
		const projection = this.projection.projection
		const viewChanged = !view.equals(this.viewMatrix)
		const projectionChanged = !projection.equals(this.projectionMatrix)
		if(!viewChanged && !projectionChanged && this.initialized) {
			return false
		}
		if(viewChanged) this.viewMatrix = view
		if(projectionChanged) this.projectionMatrix = projection
		this.initialized = true
		this.modelViewProjection = this.viewMatrix.multiply(this.projectionMatrix)
		this.modelViewProjectionInverse = undefined	// invalidate the inverse matrix
		return true
	}

	// function to give the projection of a point in the world space to a point on the screen
	public worldToScreen(v: vec3): vec2 {
		const xformedPoint = this.viewMatrix.multVec4(v.x, v.y, v.z, 1)
		const viewProj = this.projectionMatrix.multVec4(xformedPoint.x, xformedPoint.y, xformedPoint.z, xformedPoint.w)
		return new vec2(
			(viewProj.x / viewProj.w + 1) * this.projection.width / 2,
			(1 - viewProj.y / viewProj.w) * this.projection.height / 2
		)
	}

	// NDC depth [0,1] for a world-space point through the perspective projection.
	// Useful for writing gl_FragDepth in shaders that need to participate in
	// the world depth buffer while being positioned in screen space.
	// A small bias pulls the depth slightly toward the camera to prevent
	// z-fighting with coplanar geometry (e.g. labels on the earth surface).
	public worldToScreenDepth(v: vec3, bias: number = 0.00005): number {
		const xformedPoint = this.viewMatrix.multVec4(v.x, v.y, v.z, 1)
		const clip = this.projectionMatrix.multVec4(xformedPoint.x, xformedPoint.y, xformedPoint.z, xformedPoint.w)
		return Math.max(0, (clip.z / clip.w + 1) * 0.5 - bias)
	}

	public rayFrom(x: number, y: number): ray {
		// only invert the matrix if it's not already inverted
		this.modelViewProjectionInverse ??= this.modelViewProjection.invert()

		const px = x*2.0/this.projection.width - 1.0
		const py = 1.0 - y*2.0/this.projection.height
		const onear = this.modelViewProjectionInverse.multVec4(px, py, 0, 1)
		const ofar = this.modelViewProjectionInverse.multVec4(px, py, 1, 1)
		const npos = new vec3(onear.x/onear.w, onear.y/onear.w, onear.z/onear.w)
		const fpos = new vec3(ofar.x/ofar.w, ofar.y/ofar.w, ofar.z/ofar.w)
		const f_minus_n = new vec3(fpos.x - npos.x, fpos.y - npos.y, fpos.z - npos.z)
		return new ray(npos, normal(f_minus_n))
	}
}
